import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import {
  TRAINING_MODULES,
  TRAINING_STATUS,
  metaFor,
  optionsFrom,
  type TrainingRecord,
} from "@/lib/adminModules";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminTraining() {
  const [rows, setRows] = useState<TrainingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<TrainingRecord>("training_records", "farmer_name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load training records.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<TrainingRecord>) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("training_records", id, patch);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (moduleFilter !== "all" && row.module !== moduleFilter) return false;
      if (!query) return true;
      return [row.farmer_name, row.module, row.trainer || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter, moduleFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<TrainingRecord>[] = [
    {
      key: "farmer",
      header: "Farmer",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farmer_name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.module}</p>
        </div>
      ),
    },
    {
      key: "trainer",
      header: "Trainer",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{row.trainer || "—"}</span>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(TRAINING_STATUS, row.status)} /> },
    {
      key: "scheduled",
      header: "Scheduled",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.scheduled_date)}</span>
      ),
    },
    {
      key: "score",
      header: "Score",
      render: (row) => (
        <span className="text-sm text-foreground-700">{row.score > 0 ? `${row.score}%` : "—"}</span>
      ),
    },
    {
      key: "expires",
      header: "Expires",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.expires_at)}</span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Training"
        subtitle="Track every farmer's training across hospitality, safety, food hygiene, storytelling and more."
      />

      <StatCards
        stats={[
          { label: "Training records", value: rows.length, icon: "ri-graduation-cap-line", tone: "primary" },
          {
            label: "Completed",
            value: rows.filter((row) => row.status === "completed").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "In progress",
            value: rows.filter((row) => row.status === "in_progress" || row.status === "scheduled").length,
            icon: "ri-loader-4-line",
            tone: "secondary",
          },
          {
            label: "Expired",
            value: rows.filter((row) => row.status === "expired").length,
            icon: "ri-error-warning-line",
            tone: "accent",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by farmer, module, trainer…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(TRAINING_STATUS)]}
          />
          <Select
            label="Module"
            value={moduleFilter}
            onChange={setModuleFilter}
            options={[
              { value: "all", label: "All modules" },
              ...TRAINING_MODULES.map((module) => ({ value: module, label: module })),
            ]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} records
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading training records…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-graduation-cap-line"
            title={rows.length === 0 ? "No training records" : "No records match"}
            message="Scheduled and completed training modules will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.farmer_name || "Training record"}
        subtitle={selected?.module || "Module"}
      >
        {selected ? (
          <>
            <StatusPill {...metaFor(TRAINING_STATUS, selected.status)} />

            <DetailList
              items={[
                { icon: "ri-graduation-cap-line", label: "Module", value: selected.module },
                { icon: "ri-user-voice-line", label: "Trainer", value: selected.trainer || "—" },
                { icon: "ri-calendar-line", label: "Scheduled", value: formatDate(selected.scheduled_date) },
                { icon: "ri-award-line", label: "Certificate", value: selected.certificate || "Not issued" },
                { icon: "ri-calendar-check-line", label: "Expires / renewal", value: formatDate(selected.expires_at) },
              ]}
            />

            <Field label="Status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value })}
                className={fieldInput}
              >
                {optionsFrom(TRAINING_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Score">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={selected.score}
                  onChange={(event) => update(selected.id, { score: Number(event.target.value) })}
                  className="flex-1 cursor-pointer accent-primary-500"
                />
                <span className="text-sm font-semibold text-foreground-900 w-12 text-right">
                  {selected.score}%
                </span>
              </div>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}